import { NoteEditor } from '@/components/mentions/NoteEditor';
import { toast } from 'sonner';
import React, { useState, useRef, useEffect } from 'react';
import { formatTypingNames } from '@/utils/formatTypingNames';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import Dropzone from 'react-dropzone';
import axios from 'axios';
import { TYPING_HEARTBEAT_SECONDS } from '@/actions/typingIndicator';
import { useDispatch, useSelector } from 'react-redux';

import Giphy from './Giphy';
import ImagePreviews from './ImagePreviews';
import ImagePreview from '../chat/ImagePreview';

import CustomerSearch from './CustomerSearch';

import { sendTeamMessage, setTeamReply } from '../../../actions/teams';
import { quotePreview } from './ReplyQuote';
import { CUSTOMER_PHOTOS_CHANGED } from '../../../actions/customerGallery';
import { getTemplates } from '../../../actions/templates';
import TemplateSelector from '../../utils/TemplateSelector';

import getTeamsUrl from '../../../utils/getTeamsUrl';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import { IconButton } from '@/components/ui/custom/IconButton';
import TalkButton from '@/components/ui/custom/TalkButton';
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	Paperclip,
	Smile,
	AtSign,
	Play,
	BookOpen,
	CornerUpLeft,
	Contact,
	X,
	Loader2,
	Send as SendIcon,
	ArrowUp,
	ImagePlus,
	Plus,
} from 'lucide-react';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { useTheme } from '@/components/ui/theme-provider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
	ActionSheet,
	BottomSheet,
	BottomSheetTextButton,
} from '@/components/mobile';

const teamsTypingUrl = `${getTeamsUrl(
	window.location.href
)}/api/webchat/typing`;

const diffSeconds = (dt2 = new Date(), dt1 = new Date()) => {
	return (dt2.getTime() - dt1.getTime()) / 1000;
};

const acceptConfig = {
	'image/*': [], // All image files (including GIFs)
	'application/pdf': [], // PDFs
	'video/*': [], // All video files
	'text/plain': [], // Plain text files
	'.csv': [], // CSV files
	'application/msword': [], // Word documents (.doc)
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
		[], // Word documents (.docx)
	'application/vnd.ms-excel': [], // Excel files (.xls)
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Excel files (.xlsx)
};

const MIN_TEXTAREA_HEIGHT = 56;
// Phones: a one-line pill that grows to about five lines.
const PHONE_MIN_TEXTAREA_HEIGHT = 42;
const PHONE_MAX_LINES = 5;

const Composer = ({ isMobile: isMobileProp } = {}) => {
	// The dashboard passes isMobile; customer notes (runner/Comments) don't,
	// so fall back to the viewport.
	const viewportIsMobile = useIsMobile();
	const isMobile = isMobileProp ?? viewportIsMobile;
	// Phones: one "+" opens these instead of the desktop toolbar.
	const [plusSheetOpen, setPlusSheetOpen] = useState(false);
	const [mentionSheetOpen, setMentionSheetOpen] = useState(false);
	const typingIndicator = useSelector((state) => state.typingIndicator);

	const [text, setText] = useState('');
	const [attachedCustomerChip, setAttachedCustomerChip] = useState(null);
	const [attachments, setAttachments] = useState([]); // [{name, path, preview, loading, mediaUrl}
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const [talking, setTalking] = useState(false);
	const [showGiphyPicker, setShowGiphyPicker] = useState(false);
	const [openCustomerSearch, setOpenCustomerSearch] = useState(false);
	const [showTagUserPopover, setShowTagUserPopover] = useState(false);
	const [mentionedUsers, setMentionedUsers] = useState([]);
	const [templatesExpanded, setTemplatesExpanded] = useState(false);
	const textareaRef = useRef(null);
	const dropzoneRef = useRef(null);

	const { resolvedTheme: theme } = useTheme();
	const dispatch = useDispatch();

	const teams = useSelector((state) => state.teams);
	const auth = useSelector((state) => state.auth);
	const users = useSelector((state) => state.users);
	// already fetched at app boot — the selector filters to group templates
	const templates = useSelector((state) => state.templates);

	const lastTextChangeTime = useRef(new Date());

	useEffect(() => {
		if (!textareaRef.current) {
			return;
		}

		const textarea = textareaRef.current;
		// Reset height to auto to get the correct scrollHeight
		textarea.style.height = 'auto';

		const { scrollHeight } = textarea;
		const computedStyle = window.getComputedStyle(textarea);

		const parsedLineHeight = parseFloat(computedStyle.lineHeight);
		const fallbackLineHeight =
			(parseFloat(computedStyle.fontSize) || 16) * 1.2;
		const lineHeight = Number.isNaN(parsedLineHeight)
			? fallbackLineHeight
			: parsedLineHeight;

		const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
		const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

		// Max height for 8 lines of text (5 on phones), including padding.
		let maxHeight =
			(isMobile ? PHONE_MAX_LINES : 8) * lineHeight + paddingTop + paddingBottom;

		// Below 1280px wide (tablets, landscape phones) the keyboard can leave
		// a short window: keep the box to about a third of it so the thread
		// and the send button stay on screen.
		if (!isMobile && window.innerWidth < 1280) {
			const viewportHeight = window.visualViewport?.height || window.innerHeight;
			maxHeight = Math.min(
				maxHeight,
				Math.max(2 * lineHeight + paddingTop + paddingBottom, viewportHeight * 0.3)
			);
		}

		const boundedHeight = Math.max(
			isMobile ? PHONE_MIN_TEXTAREA_HEIGHT : MIN_TEXTAREA_HEIGHT,
			Math.min(scrollHeight, maxHeight)
		);

		textarea.style.height = `${boundedHeight}px`;
		textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
	}, [text, isMobile]);

  const [noteMentions, setNoteMentions] = useState([]);
  const noteRequest = useRef(crypto.randomUUID());
  const noteContext = useRef(teams._activeTeamConversation); noteContext.current = teams._activeTeamConversation;
  const noteFingerprint = useRef('');
  const [noteSaving, setNoteSaving] = useState(false);
  const savingNote = useRef(false);
  const noteDraftKey = `notepad-draft:${auth.user?._id}:${teams._activeTeamConversation}`;
  const selectedConversation = teams.teamConversations.find((conversation) => conversation._id === teams._activeTeamConversation);
  const customerNoteContext = Boolean(selectedConversation?._customer || selectedConversation?.isDirectMessage === false);
  const previousNoteContext = useRef(false);
  const [loadedDraftKey, setLoadedDraftKey] = useState(null);
  useEffect(() => {
    if (!customerNoteContext) { if (previousNoteContext.current) { setText(''); setNoteMentions([]); } previousNoteContext.current = false; return; }
    previousNoteContext.current = true;
    let draft = { text: '', mentions: [] };
    try { const saved = sessionStorage.getItem(noteDraftKey); if (saved) draft = JSON.parse(saved); } catch { /* optional storage */ }
    noteFingerprint.current = '';
    setText(draft.text); setNoteMentions(draft.mentions || []); setAttachments(draft.attachments || []); setAttachedCustomerChip(draft.attachedCustomerChip || null); noteRequest.current = draft.requestId || crypto.randomUUID(); setLoadedDraftKey(noteDraftKey);
  }, [noteDraftKey, customerNoteContext]);
  useEffect(() => {
    if (!customerNoteContext || loadedDraftKey !== noteDraftKey) return;
    const fingerprint = JSON.stringify({ text, mentions: noteMentions, attachments, attachedCustomerChip });
    if (noteFingerprint.current && noteFingerprint.current !== fingerprint) noteRequest.current = crypto.randomUUID();
    noteFingerprint.current = fingerprint;
    try { sessionStorage.setItem(noteDraftKey, JSON.stringify({ text, mentions: noteMentions, attachments, attachedCustomerChip, requestId: noteRequest.current })); } catch { /* optional storage */ }
  }, [text, noteMentions, attachments, attachedCustomerChip, customerNoteContext, loadedDraftKey, noteDraftKey]);
	const { _activeTeamConversation } = teams;
	const { _id: _business, user } = auth;
	const replyingTo = teams.replyTo?.[_activeTeamConversation];

	if (!_activeTeamConversation) return null;

	const activeTeamConversation = teams.teamConversations.find(
		(conv) => conv._id === _activeTeamConversation
	);

	if (!activeTeamConversation) return null;

	// get the full users from the users array, that are in the active conversation
	const usersInConversation = users
		.filter((u) => u._businesses.includes(activeTeamConversation._business))
		.filter((u) => {
			// if the team conversation is a customer note pad, show all users
			if (activeTeamConversation._customer) {
				return u.active;
			}
			return activeTeamConversation?._users.includes(u._id) && u.active;
		});

	const userFilteredFromConversation = activeTeamConversation?._users.find(
		(u) => u !== auth.user._id
	);

	const userFromDirectMessage = users.find(
		(u) => u._id === userFilteredFromConversation
	);

	const sendTypingIndicator = (isTyping) => {
		if (!_activeTeamConversation) {
			return;
		}

		if (!isTyping) {
			lastTextChangeTime.current = new Date(0);
		}

		try {
			axios
				.post(teamsTypingUrl, {
					isTyping,
					whoIsTyping: user.name,
					_teamConversation: _activeTeamConversation,
					at: Date.now(),
				})
				.catch((err) => {
					console.log('Error sending typing indicator:', err);
				});
		} catch (err) {
			console.log(err);
		}
	};

	// canned team messages don't fit runner notes / customer notepads, where
	// this composer also renders (via runner/Comments.tsx)
	const showTemplatesButton =
		!activeTeamConversation?.isRunner && !activeTeamConversation?._customer;

	const toggleTemplates = () => setTemplatesExpanded((prev) => !prev);

	// append rather than replace: the draft may already hold typed @mentions
	// and the [customer] chip token, which a replace would silently destroy
	const handleTemplateClick = ({ template }) => {
		setText((prevValue) => {
			const next = prevValue
				? `${prevValue}\n${template.text}`
				: template.text;
			return next.length > 10000 ? next.slice(0, 10000) : next;
		});
		setTemplatesExpanded(false);
		document.getElementById('teamTextBox')?.focus();
	};

	const getUserTemplates = () =>
		dispatch(getTemplates(Boolean(user?.isEnterpriseEnabled)));

	const deleteTemplate = async ({ template }) => {
		const res = await axios.post('/api/template/delete', { template });
		getUserTemplates();
		// the route reports failures as 200 + {error, message}; throw so
		// CreateTemplateDialog's .catch alerts and keeps the dialog open
		// (mirrors how ChatBox surfaces these)
		if (res.data?.error && res.data.message !== 'No template found') {
			throw new Error(
				res.data.message || 'There was an error deleting your template.'
			);
		}
		return res;
	};

	const notifyTyping = (inputValue) => {
		// we can only have typing indicators on known conversations
		if (_activeTeamConversation) {
			try {
				if (!inputValue.length) {
					sendTypingIndicator(false);
					return;
				}

				if (
					diffSeconds(new Date(), lastTextChangeTime.current) === 0 ||
					diffSeconds(new Date(), lastTextChangeTime.current) >
						TYPING_HEARTBEAT_SECONDS
				) {
					sendTypingIndicator(true);

					lastTextChangeTime.current = new Date();
				}
			} catch (err) {
				console.log(err);
			}
		}
	};

	const handleInputChange = (event) => {
		const inputValue = event.target.value;

		if (inputValue.length > 10000) {
			return;
		}

		setText(inputValue);
		notifyTyping(inputValue);

		// Phones: an @ typed at the start of a word opens the teammate list,
		// like the desktop popover. "name@example.com" doesn't.
		if (isMobile) {
			const caret = event.target.selectionStart ?? inputValue.length;
			const typedAtSign =
				inputValue.length === text.length + 1 &&
				inputValue[caret - 1] === '@' &&
				(caret === 1 || /\s/.test(inputValue[caret - 2]));
			if (typedAtSign) {
				setMentionSheetOpen(true);
			}
		}
	};

	// Phones: puts a picked teammate into the draft at the caret and keeps
	// them for the send, as handleUserClick does for the desktop list.
	const insertMention = (member, { withAtSign = false } = {}) => {
		const el = textareaRef.current;
		setText((prevValue) => {
			const caret = Math.min(el?.selectionStart ?? prevValue.length, prevValue.length);
			const next = `${prevValue.slice(0, caret)}${withAtSign ? '@' : ''}${member.name} ${prevValue.slice(caret)}`;
			return next.length > 10000 ? prevValue : next;
		});
		setMentionedUsers((prev) =>
			prev.some((u) => u._id === member._id) ? prev : [...prev, member]
		);
		setTimeout(() => el?.focus(), 0);
	};

	const addEmoji = (emoji) => {
		const newMessage = `${text}${emoji.native}`;
		setText(newMessage);
    if (customerNoteContext) { noteRequest.current = crypto.randomUUID(); try { sessionStorage.setItem(noteDraftKey, JSON.stringify({ text: newMessage, mentions: noteMentions, attachments, attachedCustomerChip, requestId: noteRequest.current })); } catch { /* optional storage */ } }
		setShowEmojiPicker(false);
		const el = document.getElementById('teamTextBox');

		if (el) {
			el.focus();
		}
	};

	// make sure the mentioned users are in the text
	const getMentionedUsers = (text) => {
		const _mentionedUsers = [];

		mentionedUsers.forEach((mentionedUser) => {
			if (text.includes(`@${mentionedUser.name}`)) {
				_mentionedUsers.push(mentionedUser._id);
			}
		});

		return _mentionedUsers;
	};

	const toggleEmojiPicker = () => {
		setShowEmojiPicker(!showEmojiPicker);
	};

	const toggleGiphyPicker = () => {
		setShowGiphyPicker(!showGiphyPicker);
	};

	const typingNames = typingIndicator
		.filter((t) => t._teamConversation === _activeTeamConversation)
		.map((t) => t.whoIsTyping);
	const typingConvo = typingNames.length > 0;

	// The linked customer is shown as a chip and sent with the message, not
	// typed into it
	const handleSetAttachedCustomerChip = (customer) => {
		setAttachedCustomerChip(customer);
	};

	const handleSendMessage = async () => {
    if (savingNote.current) return;
    // Enter bypasses the disabled Send button, so wait for uploads here too
    if (attachments.some((a) => a.loading)) return;
    const customerNote = customerNoteContext;
    const draftKey = noteDraftKey;
    const fingerprint = JSON.stringify({ text, mentions: noteMentions, attachments, attachedCustomerChip });
    if (noteFingerprint.current && noteFingerprint.current !== fingerprint) noteRequest.current = crypto.randomUUID();
    noteFingerprint.current = fingerprint;
    const requestId = noteRequest.current;
    const conversationId = _activeTeamConversation;
    if (customerNote && !text.trim() && !attachments.length) return;
    savingNote.current = true; setNoteSaving(true);
    try {
		const formattedAttachments = attachments.map((a) => ({
			name: a.name || a.path,
			url: a.mediaUrl,
			type: a.type,
			size: a.size,
		}));

		const _mentionedUsers = getMentionedUsers(text);

		const body = {
			text,
      ...(customerNote ? { mentions: noteMentions.filter((m) => text.slice(m.start, m.end) === `@${m.name}`), requestId } : {}),
			_teamConversation: _activeTeamConversation,
			_mentionedUsers,
			_business: activeTeamConversation._business || _business,
			attachments: formattedAttachments,
			_attachedCustomer: attachedCustomerChip
				? attachedCustomerChip._customer
				: undefined,
			_replyTo: replyingTo?._id,
		};

		sendTypingIndicator(false);
    const result = await dispatch(sendTeamMessage(body));
    savingNote.current = false;
    if (result?.error) { toast.error(result.message || 'Could not save your note.'); return; }
    // photos sent with a linked customer are saved to them on the server
    if (attachedCustomerChip && formattedAttachments.some((a) => a.type?.startsWith('image/'))) {
      setTimeout(() => window.dispatchEvent(new CustomEvent(CUSTOMER_PHOTOS_CHANGED, { detail: { _customer: attachedCustomerChip._customer } })), 2500);
    }
    dispatch(setTeamReply({ _teamConversation: conversationId, message: null }));
    if (customerNote) { try { const saved = JSON.parse(sessionStorage.getItem(draftKey) || 'null'); if (saved?.requestId === requestId) sessionStorage.removeItem(draftKey); } catch { /* optional storage */ } }
    if (noteContext.current !== conversationId || (customerNote && noteRequest.current !== requestId)) return;
    if (customerNote) { setNoteMentions([]); noteRequest.current = crypto.randomUUID(); }
		setMentionedUsers([]);
		setText('');
		setAttachments([]);
		setAttachedCustomerChip(null);
    } catch (error) { toast.error(error.response?.data?.message || 'Could not save your note. Your draft is safe.'); }
    finally { savingNote.current = false; setNoteSaving(false); }
	};

	const addAtSignToText = () => {
		setText((prevValue) => {
			const cursorPosition = textareaRef.current.selectionStart;
			const newText =
				prevValue.slice(0, cursorPosition) +
				'@' +
				prevValue.slice(cursorPosition);

			return newText;
		});
	};

	const handleEnterKeyPress = (event) => {
		// Phones: Return is a new line, like Messages; the Send button sends.
		if (event.key === 'Enter' && !event.shiftKey && !isMobile) {
			event.preventDefault(); // Prevents adding a new line
			handleSendMessage();
		}

		// Phones pick up a typed @ in handleInputChange instead (Android
		// keyboards don't report the key).
		if (event.key === '@' && !isMobile) {
			event.preventDefault();
			addAtSignToText();
			setShowTagUserPopover(true);
		}
	};

	const handleUserClick = ({ user }) => {
		// add user.name to the text where the cursor is
		setText((prevValue) => {
			const cursorPosition = textareaRef.current.selectionStart;
			const newText =
				prevValue.slice(0, cursorPosition) +
				user.name +
				prevValue.slice(cursorPosition);

			return newText;
		});

		setShowTagUserPopover(false);

		// focus on the textarea
		const el = document.getElementById('teamTextBox');

		if (el) {
			el.focus();
		}

		// don't add the user if they are already mentioned
		if (mentionedUsers.filter((u) => u._id === user._id).length > 0) {
			return;
		}

		// store the users for post request
		setMentionedUsers((prevMentionedUsers) => {
			return [...prevMentionedUsers, user];
		});
	};

	const removeAttachment = (attachment) => {
		const newAttachments = attachments.filter(
			(a) => a.path !== attachment.path
		);

		setAttachments(newAttachments);
	};

	const openFileSelector = () => {
		// Note that the ref is set async,
		// so it might be null at some point
		if (dropzoneRef.current) {
			dropzoneRef.current.open();
		}
	};

	// Submit image file to be uploaded to webchat server, then webchat server uploads to S3
	// S3 returns a public url for the uploaded image.
	// send this mediaUrl to twilio to include in the message to the customer
	const uploadAttachments = async (newAttachments) => {
		let allAttachments = [];

		try {
			for await (const attachment of newAttachments) {
				const s3UploadRes = await axios.get(
					`${getTeamsUrl(
						window.location.href
					)}/api/presigned-upload-url`,
					{
						params: {
							fileType: attachment.type,
							_teamConversation: _activeTeamConversation,
						},
					}
				);

				if (!s3UploadRes.data || s3UploadRes.data.error) {
					throw new Error('Could not get an upload URL');
				}

				const { uploadUrl, mediaUrl } = s3UploadRes.data;

				// upload directly to s3
				const s3PutRes = await fetch(uploadUrl, {
					method: 'PUT',
					body: attachment,
					headers: {
						'Content-Type': attachment.type,
					},
				});

				if (!s3PutRes.ok) {
					throw new Error(`S3 upload failed with ${s3PutRes.status}`);
				}

				const updatedAttachment = {
					...attachment,
					name: attachment.name || attachment.path,
					type: attachment.type,
					size: attachment.size,
					preview: URL.createObjectURL(attachment),
					loading: false,
					mediaUrl,
				};

				allAttachments = [...allAttachments, updatedAttachment];
			}
			setAttachments(allAttachments);
		} catch (err) {
			console.log('upload error', err);

			// Keep the files that uploaded and drop the rest, so nothing is
			// left loading and a file that never reached S3 cannot be sent.
			setAttachments(allAttachments);
			toast.error(
				allAttachments.length
					? 'Some files could not be uploaded. Please try adding them again.'
					: 'Your file could not be uploaded. Please try again.'
			);

			return null;
		}
	};

	const getDropRejectionMessage = (fileRejections = []) => {
		const codes = fileRejections.flatMap(({ errors }) =>
			errors.map((e) => e.code)
		);

		if (codes.includes('too-many-files')) {
			return 'You can attach up to 4 files at once.';
		}
		if (codes.includes('file-too-large')) {
			return 'Files must be smaller than 20MB.';
		}
		if (codes.includes('file-invalid-type')) {
			return 'That file type is not supported.';
		}
		return 'That file could not be attached.';
	};

	// if not a group chat and user is not active, don't show input

	if (
		!userFromDirectMessage?.active &&
		activeTeamConversation?.isDirectMessage
	) {
		return (
			<div className="overflow-hidden h-fit bg-accent p-2 max-md:pb-[max(0.5rem,var(--safe-bottom))]">
				<p className="py-3 text-center">
					This user account no longer exists
				</p>
			</div>
		);
	}

	if (!activeTeamConversation) {
		return null;
	}

	const placeholder = attachedCustomerChip
		? `Add a message about ${attachedCustomerChip.name || 'this customer'}…`
		: activeTeamConversation?.isRunner
			? 'Leave a note...'
			: activeTeamConversation?.isDirectMessage
				? `Message ${userFromDirectMessage.name}`
				: `Message ${activeTeamConversation?.title}`;

	const handleDrop = async (acceptedFiles) => {
		// A fully rejected drop must not clear the files already attached
		if (!acceptedFiles.length) {
			return;
		}

		setAttachments(
			acceptedFiles.map((file) => {
				return {
					...file,
					preview: URL.createObjectURL(file),
					loading: true,
					name: file.name || file.path,
					size: file.size,
					type: file.type,
				};
			})
		);

		await uploadAttachments(acceptedFiles);
	};

	const handleNoteChange = (value, mentions) => {
		setText(value);
		notifyTyping(value);
		setNoteMentions(mentions);
		noteRequest.current = crypto.randomUUID();
		try {
			sessionStorage.setItem(
				noteDraftKey,
				JSON.stringify({
					text: value,
					mentions,
					attachments,
					attachedCustomerChip,
					requestId: noteRequest.current,
				})
			);
		} catch {
			/* optional storage */
		}
	};

	// Phones: a "+" button, a pill text field and a round send button, like
	// the customer thread. Return adds a line; the tools sit in one sheet.
	// The draft, reply and attachments live in this component's state, so
	// they survive switching between this and the desktop toolbar.
	const renderPhoneComposer = () => {
		const canSend =
			!noteSaving &&
			!attachments.some((a) => a.loading) &&
			(attachments.length > 0 || Boolean(text.trim()));
		const replyName = replyingTo
			? users.find((u) => u._id === replyingTo._user)?.name || 'Unknown User'
			: '';
		const mentionItems = (withAtSign) => [
			{
				id: 'everyone',
				label: '@everyone',
				description: 'Alert everyone in this chat',
				searchText: 'everyone',
				onSelect: () =>
					insertMention({ _id: 'everyone', name: 'everyone' }, { withAtSign }),
			},
			...usersInConversation.map((member) => ({
				id: member._id,
				label: member.name,
				searchText: member.name,
				onSelect: () => insertMention(member, { withAtSign }),
			})),
		];
		const toolItems = [
			{ id: 'file', label: 'Photo or file', icon: ImagePlus, onSelect: openFileSelector },
			{ id: 'gif', label: 'GIF', icon: Play, onSelect: () => setShowGiphyPicker(true) },
			{
				id: 'customer',
				label: 'Link a customer',
				icon: Contact,
				onSelect: () => setOpenCustomerSearch(true),
			},
			showTemplatesButton && {
				id: 'templates',
				label: 'Templates',
				icon: BookOpen,
				onSelect: () => setTemplatesExpanded(true),
			},
			!customerNoteContext && {
				id: 'mention',
				label: 'Mention a teammate',
				icon: AtSign,
				page: 'mention',
			},
		].filter(Boolean);
		const rowClass =
			'mb-1.5 flex min-h-11 items-center gap-2 rounded-xl bg-muted/70 pl-3 dark:bg-zinc-800/80';
		const rowCloseClass =
			'flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

		return (
			<div className="flex flex-col bg-background px-2 pb-[max(0.375rem,var(--safe-bottom))] pt-1.5 kb-open:pb-1.5">
				{showTemplatesButton && (
					<TemplateSelector
						templates={templates}
						templateKind="groupChat"
						handleTemplateClick={handleTemplateClick}
						templatesExpanded={templatesExpanded}
						showEmojiPicker={showEmojiPicker}
						toggleTemplates={toggleTemplates}
						onOpenChange={setTemplatesExpanded}
						deleteTemplate={deleteTemplate}
						getUserTemplates={getUserTemplates}
						renderDialog
					/>
				)}
				{typingConvo && (
					<p
						className="truncate px-2 pb-1 text-xs text-muted-foreground"
						aria-live="polite"
					>
						{formatTypingNames(typingNames)}…
					</p>
				)}
				{attachments.length > 0 && (
					<div className="scroll-touch -mx-2 mb-1.5 flex gap-2 overflow-x-auto px-2 pb-0.5 [&>*]:w-56 [&>*]:shrink-0">
						{attachments.map((attachment, index) => (
							<ImagePreview
								key={attachment.path || attachment.mediaUrl || index}
								attachment={attachment}
								removeAttachment={removeAttachment}
							/>
						))}
					</div>
				)}
				{replyingTo && (
					<div className={rowClass}>
						<CornerUpLeft className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						<div className="min-w-0 flex-1 py-1.5">
							<p className="truncate text-[13px] leading-4 text-muted-foreground">
								Replying to <span className="font-medium text-foreground">{replyName}</span>
							</p>
							<p className="truncate text-[13px] leading-5 text-muted-foreground">
								{quotePreview({
									text: replyingTo.text,
									attachments: replyingTo.attachments || [],
									customer: replyingTo._attachedCustomer,
								})}
							</p>
						</div>
						<button
							type="button"
							aria-label="Cancel reply"
							onClick={() =>
								dispatch(
									setTeamReply({
										_teamConversation: _activeTeamConversation,
										message: null,
									})
								)
							}
							className={rowCloseClass}
						>
							<X className="size-4" aria-hidden />
						</button>
					</div>
				)}
				{attachedCustomerChip && (
					<div className={rowClass}>
						<Contact className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						<p className="min-w-0 flex-1 truncate py-1.5 text-[13px] text-muted-foreground">
							Linked customer{' '}
							<span className="font-medium text-foreground">
								{attachedCustomerChip.name || 'Customer'}
							</span>
						</p>
						<button
							type="button"
							aria-label="Remove linked customer"
							onClick={() => setAttachedCustomerChip(null)}
							className={rowCloseClass}
						>
							<X className="size-4" aria-hidden />
						</button>
					</div>
				)}

				<Dropzone
					onDrop={handleDrop}
					onDropRejected={(fileRejections) =>
						toast.error(getDropRejectionMessage(fileRejections))
					}
					ref={dropzoneRef}
					noClick
					noKeyboard
					maxFiles={4}
					maxSize={19999999}
					accept={acceptConfig}
				>
					{({ getRootProps, getInputProps, isDragActive }) => (
						<div
							className={cn(
								'flex items-end gap-1',
								isDragActive && 'rounded-[22px] ring-2 ring-primary'
							)}
							{...getRootProps()}
							data-onboarding={showTemplatesButton ? 'teams-composer' : undefined}
						>
							<input {...getInputProps()} />
							<button
								type="button"
								aria-label="Add a photo, GIF, customer or template"
								aria-haspopup="dialog"
								data-onboarding={showTemplatesButton ? 'teams-link-customer' : undefined}
								onClick={() => setPlusSheetOpen(true)}
								className="flex size-11 shrink-0 items-center justify-center rounded-full active:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							>
								<span className="flex size-8 items-center justify-center rounded-full bg-muted text-foreground">
									<Plus className="size-5" aria-hidden />
								</span>
							</button>
							<div className="flex min-h-11 min-w-0 flex-1 items-end gap-1 rounded-[22px] border border-input bg-background pl-3.5 pr-1 transition-colors focus-within:border-foreground/40">
								<div className="min-w-0 flex-1">
									{customerNoteContext ? (
										<NoteEditor
											embedded
											compact
											submitOnEnter={false}
											pinCaretToEnd={talking}
											key={_activeTeamConversation}
											value={text}
											mentions={noteMentions}
											_teamConversation={
												!activeTeamConversation._customer
													? _activeTeamConversation
													: undefined
											}
											_customer={activeTeamConversation._customer}
											_business={activeTeamConversation._business}
											placeholder={
												attachedCustomerChip
													? `Add a message about ${attachedCustomerChip.name || 'this customer'}…`
													: activeTeamConversation._customer || activeTeamConversation.isRunner
														? 'Note. Type @ to tag'
														: 'Message. Type @ to tag'
											}
											disabled={noteSaving}
											onSubmit={handleSendMessage}
											onChange={handleNoteChange}
										/>
									) : (
										<Textarea
											ref={textareaRef}
											id="teamTextBox"
											aria-label="Message"
											rows={1}
											className="block min-h-[42px] resize-none border-0 bg-transparent px-0 py-[9px] text-base leading-6 shadow-none focus-visible:ring-0 md:text-base"
											value={text}
											onChange={handleInputChange}
											onKeyDown={handleEnterKeyPress}
											variant="standard"
											placeholder={placeholder}
											autoComplete="off"
											autoCapitalize="sentences"
											enterKeyHint="enter"
											spellCheck
										/>
									)}
								</div>
								<button
									type="button"
									// group chats share the notes editor, but they send messages
									aria-label={
										selectedConversation?._customer || selectedConversation?.isRunner
											? 'Add note'
											: 'Send message'
									}
									disabled={!canSend}
									// Keeps the text field focused, so the keyboard stays up after sending.
									onMouseDown={(event) => event.preventDefault()}
									onClick={handleSendMessage}
									className={cn(
										'relative mb-1 flex size-9 shrink-0 items-center justify-center rounded-full transition-colors before:absolute before:-inset-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
										canSend
											? 'bg-primary text-primary-foreground'
											: 'bg-muted text-muted-foreground'
									)}
								>
									{noteSaving ? (
										<Loader2 className="size-[18px] animate-spin" aria-hidden />
									) : (
										<ArrowUp className="size-5" strokeWidth={2.5} aria-hidden />
									)}
								</button>
							</div>
						</div>
					)}
				</Dropzone>

				<ActionSheet
					open={plusSheetOpen}
					onOpenChange={setPlusSheetOpen}
					title="Add to message"
					items={toolItems}
					pages={
						customerNoteContext
							? []
							: [
									{
										id: 'mention',
										title: 'Mention',
										searchable: true,
										searchPlaceholder: 'Search teammates',
										emptyText: 'No teammates match',
										sections: [{ items: mentionItems(true) }],
									},
								]
					}
				/>
				{!customerNoteContext && (
					<ActionSheet
						open={mentionSheetOpen}
						onOpenChange={setMentionSheetOpen}
						title="Mention a teammate"
						items={mentionItems(false)}
					/>
				)}
				<BottomSheet
					open={showGiphyPicker}
					onOpenChange={setShowGiphyPicker}
					title="GIFs"
					headerLeft={
						<BottomSheetTextButton onClick={() => setShowGiphyPicker(false)}>
							Cancel
						</BottomSheetTextButton>
					}
					bodyClassName="px-3 [&_input]:!text-base"
				>
					<Giphy
						setAttachments={setAttachments}
						toggleGiphyPicker={() => setShowGiphyPicker(false)}
					/>
				</BottomSheet>
				<BottomSheet
					open={openCustomerSearch}
					onOpenChange={setOpenCustomerSearch}
					title="Link a customer"
					size="large"
					headerLeft={
						<BottomSheetTextButton onClick={() => setOpenCustomerSearch(false)}>
							Cancel
						</BottomSheetTextButton>
					}
					bodyClassName="px-2"
				>
					<CustomerSearch
						handleSetAttachedCustomerChip={handleSetAttachedCustomerChip}
						isEnterpriseEnabled={user.isEnterpriseEnabled}
						setOpenCustomerSearch={setOpenCustomerSearch}
					/>
				</BottomSheet>
			</div>
		);
	};

	if (isMobile) {
		return renderPhoneComposer();
	}

	return (
		<div className="px-3 pb-2 pt-2">
			<TooltipProvider>
				{showTemplatesButton && (
					<TemplateSelector
						templates={templates}
						templateKind="groupChat"
						handleTemplateClick={handleTemplateClick}
						templatesExpanded={templatesExpanded}
						showEmojiPicker={showEmojiPicker}
						toggleTemplates={toggleTemplates}
						onOpenChange={setTemplatesExpanded}
						deleteTemplate={deleteTemplate}
						getUserTemplates={getUserTemplates}
						renderDialog
					/>
				)}
				<div
					style={{
						display: 'flex',
						alignItems: 'row',
						justifyContent: 'space-between',
						width: '100%',
					}}
				>
					<ImagePreviews
						attachments={attachments}
						removeAttachment={removeAttachment}
					/>
				</div>
				<div className="relative h-0">
					{typingConvo && (
						<p className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 truncate bg-background px-1 pb-0.5 text-[11px] leading-4 text-muted-foreground">
							{formatTypingNames(typingNames)}…
						</p>
					)}
				</div>

				<Dropzone
					onDrop={handleDrop}
					onDropRejected={(fileRejections) =>
						toast.error(getDropRejectionMessage(fileRejections))
					}
					ref={dropzoneRef}
					noClick
					noKeyboard
					maxFiles={4}
					maxSize={19999999}
					accept={acceptConfig}
				>
					{({ getRootProps, getInputProps, isDragActive }) => {
						return (
							<div
								className={cn(
									'dropzone overflow-hidden rounded-2xl border border-foreground/30 bg-background transition-colors focus-within:border-foreground/60 focus-within:ring-1 focus-within:ring-ring/30',
									// below 1280px (landscape phones, tablets) the group message
									// box grows to a third of the window at most, so the keyboard
									// leaves room for the thread
									'max-xl:[&_[contenteditable=true]]:max-h-[max(5rem,calc(var(--app-height)*0.3))]',
									isDragActive && 'border-dashed border-primary'
								)}
								{...getRootProps()}
								// the team chat walkthrough points here, not at runner or customer notes
								data-onboarding={showTemplatesButton ? 'teams-composer' : undefined}
							>
								{/* the message being replied to, as the first row of the box */}
								{replyingTo && (
									<div className="team-composer-row flex items-center gap-2 border-b border-border/60 px-3 py-2">
										<CornerUpLeft className="size-3.5 shrink-0 text-muted-foreground" />
										<div className="min-w-0 flex-1">
											<p className="text-[12px] leading-4 text-muted-foreground">
												Replying to{' '}
												<span className="font-medium text-foreground">
													{users.find((u) => u._id === replyingTo._user)?.name ||
														'Unknown User'}
												</span>
											</p>
											<p className="truncate text-[13px] leading-5 text-muted-foreground">
												{quotePreview({
													text: replyingTo.text,
													attachments: replyingTo.attachments || [],
													customer: replyingTo._attachedCustomer,
												})}
											</p>
										</div>
										<button
											type="button"
											aria-label="Cancel reply"
											onClick={() =>
												dispatch(
													setTeamReply({
														_teamConversation: _activeTeamConversation,
														message: null,
													})
												)
											}
											className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted-foreground/20 hover:text-foreground"
										>
											<X className="size-3.5" />
										</button>
									</div>
								)}
								{/* the linked customer, sent with the message */}
								{attachedCustomerChip && (
									<div className="team-composer-row flex items-center gap-2 border-b border-border/60 px-3 py-2">
										<Contact className="size-3.5 shrink-0 text-muted-foreground" />
										<p className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
											Linked customer{' '}
											<span className="font-medium text-foreground">
												{attachedCustomerChip.name || 'Customer'}
											</span>
										</p>
										<button
											type="button"
											aria-label="Remove linked customer"
											onClick={() => setAttachedCustomerChip(null)}
											className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted-foreground/20 hover:text-foreground"
										>
											<X className="size-3.5" />
										</button>
									</div>
								)}
                {customerNoteContext ? <NoteEditor embedded pinCaretToEnd={talking} key={_activeTeamConversation} value={text} mentions={noteMentions}
                  _teamConversation={!activeTeamConversation._customer ? _activeTeamConversation : undefined}
                  _customer={activeTeamConversation._customer} _business={activeTeamConversation._business}
                  placeholder={attachedCustomerChip ? `Add a message about ${attachedCustomerChip.name || 'this customer'}…` : undefined}
                  disabled={noteSaving} onSubmit={handleSendMessage} onChange={handleNoteChange} /> : (								<Textarea
									ref={textareaRef}
									className="min-h-[56px] resize-none border-0 bg-transparent px-3 py-2 pb-0 text-[15px] leading-6 shadow-none focus-visible:ring-0 md:text-[15px]"
									value={text}
									id="teamTextBox"
									onChange={handleInputChange}
									onKeyDown={handleEnterKeyPress}
									variant="standard"
									placeholder={placeholder}
								/>)}

								<div className="flex items-center gap-0.5 p-2 pt-1">
									<TalkButton
										value={text}
										maxLength={customerNoteContext ? 10000 : 1600}
										disabled={noteSaving}
										onChange={setText}
										onListeningChange={setTalking}
									/>
									{!customerNoteContext && (<Popover
										open={showTagUserPopover}
										onOpenChange={setShowTagUserPopover}
									>
										<PopoverTrigger>
											<IconButton
												toolTipTitle="Alert user"
												toolTipSide="top"
												onClick={() => {
													// add an @ sign to the text where the cursor is
													addAtSignToText();

													setShowTagUserPopover(
														!showTagUserPopover
													);
												}}
											>
												<AtSign className="size-4" />
											</IconButton>
										</PopoverTrigger>
										<PopoverContent className="w-80">
											<Command>
												<CommandInput placeholder="Search users..." />
												<CommandList>
													<CommandGroup>
														<CommandItem
															key={'@everyone'}
															value={'everyone'}
															onSelect={() => {
																handleUserClick(
																	{
																		user: {
																			name: 'everyone',
																			_id: 'everyone',
																		},
																	}
																);
															}}
														>
															{'@everyone'}
														</CommandItem>
														{usersInConversation &&
															usersInConversation.map(
																(user) => (
																	<CommandItem
																		key={
																			user._id
																		}
																		value={
																			user.name
																		}
																		onSelect={() => {
																			handleUserClick(
																				{
																					user,
																				}
																			);
																		}}
																	>
																		{
																			user.name
																		}
																	</CommandItem>
																)
															)}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>)}

									<Popover
										open={openCustomerSearch}
										onOpenChange={setOpenCustomerSearch}
									>
										<PopoverTrigger>
											<IconButton
												toolTipTitle="Link a customer"
												toolTipSide="top"
												aria-label="Link a customer"
												data-onboarding={showTemplatesButton ? 'teams-link-customer' : undefined}
												onClick={() =>
													setOpenCustomerSearch(true)
												}
											>
												<Contact className="size-4" />
											</IconButton>
										</PopoverTrigger>
										<PopoverContent
											// opens over the message box, not the conversation list
											side="top"
											align="start"
											sideOffset={8}
											className="w-80 overflow-hidden rounded-xl p-0 shadow-lg"
										>
											<CustomerSearch
												handleSetAttachedCustomerChip={
													handleSetAttachedCustomerChip
												}
												isEnterpriseEnabled={
													user.isEnterpriseEnabled
												}
												setOpenCustomerSearch={
													setOpenCustomerSearch
												}
											/>
										</PopoverContent>
									</Popover>

									{showTemplatesButton && (
										<IconButton
											toolTipTitle="Templates"
											toolTipSide="top"
											aria-label="Use template"
											data-testid="teams-templates-button"
											onClick={toggleTemplates}
										>
											<BookOpen className="size-4" />
										</IconButton>
									)}

									<div>
										<div>
											<input {...getInputProps()} />

											<IconButton
												toolTipTitle="Attach file"
												toolTipSide="top"
												aria-label="Upload picture"
												onClick={openFileSelector}
											>
												<Paperclip className="size-4" />
											</IconButton>
										</div>
									</div>
									<Popover
										open={showGiphyPicker}
										onOpenChange={toggleGiphyPicker}
									>
										<PopoverTrigger asChild>
											<IconButton
												toolTipTitle="Giphy"
												toolTipSide="top"
											>
												<Play className="size-4" />
											</IconButton>
										</PopoverTrigger>
										<PopoverContent
											align="end"
											className="sm:w-[250px] md:w-[400px] outline-0 m-0"
										>
											<Giphy
												setAttachments={setAttachments}
												toggleGiphyPicker={
													toggleGiphyPicker
												}
											/>
										</PopoverContent>
									</Popover>
									<Popover
										open={showEmojiPicker}
										onOpenChange={toggleEmojiPicker}
									>
										<PopoverTrigger asChild>
											<IconButton
												toolTipTitle="Emoji"
												toolTipSide="top"
											>
												<Smile className="size-4" />
											</IconButton>
										</PopoverTrigger>
										<PopoverContent
											align="end"
											className="p-0 bg-transparent border-0 outline-0 m-0"
										>
											<Picker
												onEmojiSelect={addEmoji}
												showPreview={false}
												showSkinTones={false}
												useButton={false}
												data={data}
												theme={theme}
											/>
										</PopoverContent>
									</Popover>
									<Button
										size="sm"
										onClick={handleSendMessage}
										aria-label="Send"
										className={cn(
											'ml-auto h-8 rounded-xl px-3 shadow-none',
											!attachments.length &&
												!text.trim() &&
												'bg-muted text-muted-foreground hover:bg-muted'
										)}
										disabled={
											noteSaving ||
											attachments.find(
												(a) => a.loading
											) ||
											(!attachments.length && !text.trim())
										}
									>
										{noteSaving ? (
											<Loader2 className="size-4 animate-spin" />
										) : (
											<SendIcon className="size-4" />
										)}
									</Button>
								</div>
							</div>
						);
					}}
				</Dropzone>
			</TooltipProvider>
		</div>
	);
};

export default Composer;
